import React from 'react';

import { Lato } from 'next/font/google';
import Link from 'next/link';

import { faChevronLeft, faChevronRight, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { EVENT_NAME, useMoengageEventTracker } from '../../../hooks/useMoengage';

import { CONTINUE_PURCHASE_PRODUCTS_STORAGE_KEY } from '../../../constants/api';

import ProductCard from '../../ProductCard';
import styles from './ContinuePurchaseModal.module.scss';

const lato = Lato({ variable: '--font-lato', subsets: ['latin'], weight: ['100', '300', '400', '700', '900'] });

function ContinuePurchaseModal() {
  const prevRef = React.useRef();
  const nextRef = React.useRef();

  const trackMoengageEvent = useMoengageEventTracker();

  const [swiper, setSwiper] = React.useState();
  const [isOpen, setIsOpen] = React.useState(false);
  const [products, setProducts] = React.useState([]);

  React.useEffect(() => {
    if (swiper?.params) {
      swiper.params.navigation.prevEl = prevRef.current;
      swiper.params.navigation.nextEl = nextRef.current;
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiper]);

  React.useEffect(() => {
    if (!isOpen) return;

    let continuePurchaseProducts = window.localStorage.getItem(CONTINUE_PURCHASE_PRODUCTS_STORAGE_KEY) || '[]';
    try {
      continuePurchaseProducts = JSON.parse(continuePurchaseProducts);
    } catch {
      continuePurchaseProducts = [];
    }

    if (continuePurchaseProducts.length) {
      setProducts(continuePurchaseProducts.reverse());
    }
  }, [isOpen]);

  const handleOnWindowMessage = React.useCallback((event) => {
    if (typeof event.data.showContinuePurchase !== 'undefined') {
      setIsOpen(!!event.data.showContinuePurchase);
    }
  }, []);

  React.useEffect(() => {
    window.addEventListener('message', handleOnWindowMessage);

    return () => window.removeEventListener('message', handleOnWindowMessage);
  }, [handleOnWindowMessage]);

  const handleOnProductClick = (product) => {
    setIsOpen(false);
    trackMoengageEvent(EVENT_NAME.CONTINUE_PURCHASE_CLICKED, product);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Portal className={lato.variable}>
        <Dialog.Overlay className={styles.dialogOverlay} />

        <Dialog.Content className={classNames(lato.className, styles.dialogContent)}>
          <Dialog.Title asChild>
            <div className={styles.dialogHeaderWrapper}>
              <div className={styles.dialogHeader}>
                <h4 className={styles.dialogTitle}>Continue Purchase</h4>
                <span className={styles.dialogSubtitle}>Recently explored courses appear in this section</span>
              </div>

              <Dialog.Close asChild>
                <button type="button">
                  <FontAwesomeIcon icon={faTimes} fontSize={24} />
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Title>

          <Dialog.Description className={styles.dialogDesc}>
            <Swiper
              modules={[Navigation]}
              spaceBetween={16}
              slidesPerView="auto"
              slidesOffsetBefore={5}
              slidesOffsetAfter={5}
              breakpoints={{
                900: {
                  slidesPerView: 3,
                  slidesOffsetBefore: 8,
                  slidesOffsetAfter: 8,
                },
                600: {
                  slidesPerView: 2,
                  slidesOffsetBefore: 0,
                },
                350: {
                  slidesPerView: 1,
                  slidesOffsetBefore: 17,
                  width: 280,
                },
              }}
              onSwiper={setSwiper}
            >
              {products.map((product) => (
                <SwiperSlide key={product.courseId}>
                  <ProductCard
                    course={product}
                    as={Link}
                    href={`/course-detail/${product.slug}`}
                    onClick={handleOnProductClick}
                    showPrice
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            <div className={styles.sliderNavigationWrapper} data-testid="continue-purchase-nav-wrapper">
              <button type="button" ref={prevRef} aria-label="Prev Slide" data-testid="continue-purchase-prev-slide">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button type="button" ref={nextRef} aria-label="Next Slide" data-testid="continue-purchase-next-slide">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </Dialog.Description>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default ContinuePurchaseModal;
